import { useEffect, useRef, useState } from "react";
import { Layers, UserCheck, Rocket, Wallet } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const Eyebrow = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--ember)]">
    {children}
  </span>
);

const VALUE_ITEMS = [
  { icon: Layers, t: "One department, not five vendors" },
  { icon: UserCheck, t: "AI does the work, a human answers for it" },
  { icon: Rocket, t: "Live in 30 days" },
  { icon: Wallet, t: "One flat monthly fee" },
];

const useCountUp = (target, { duration = 1400, start = false } = {}) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
};

// "What We Do" value pitch panel — one department vs. hiring the team yourself.
export const WhatWeDo = () => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const left = useCountUp(10000, { start: inView, duration: 1700 });
  const right = useCountUp(500, { start: inView, duration: 1200 });

  return (
    <section
      ref={ref}
      data-testid="home-what-we-do"
      className="relative overflow-hidden bg-[var(--bg-base)] py-24 text-[var(--ink)] md:py-32"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <Reveal><Eyebrow>What we do</Eyebrow></Reveal>
        <Reveal delay={100}>
          <h2
            style={{ fontSize: "clamp(38px, 6.5vw, 80px)", letterSpacing: "-0.025em", lineHeight: 1.04 }}
            className="mt-7 max-w-3xl font-semibold text-[var(--ink)]"
          >
            We install the AI team you'd otherwise hire.
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p className="mt-4 max-w-2xl text-lg text-[var(--muted)]">
            No tools to learn, no vendors to chase. One department, run for you,
            reporting real wins.
          </p>
        </Reveal>

        {/* 4-item value row */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="what-we-do-values">
          {VALUE_ITEMS.map((item, i) => (
            <Reveal key={item.t} delay={i * 90}>
              <div className="flex h-full items-start gap-4 rounded-2xl border border-black/10 bg-[var(--bg-contrast)] p-6 transition-transform duration-300 hover:-translate-y-1">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                  <item.icon size={20} />
                </span>
                <p className="text-[15px] font-semibold leading-snug text-[var(--ink)]">{item.t}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Two-column dollar comparison */}
        <div className="mt-14 grid gap-4 md:grid-cols-2" data-testid="what-we-do-pricing">
          {/* Left — heavy, the old way */}
          <div
            className="rounded-3xl border border-black/10 bg-[var(--bg-contrast)] p-8 transition-all duration-700 ease-out sm:p-10"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
            }}
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
              Hiring the team yourself
            </p>
            <p
              style={{ fontSize: "clamp(44px, 6.5vw, 88px)", letterSpacing: "-0.03em", lineHeight: 1 }}
              className="mt-4 font-semibold text-[var(--ink)]/40"
              data-testid="price-left"
            >
              ${left.toLocaleString()}
              <span className="text-lg font-medium text-[var(--muted)]">/mo</span>
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-[var(--muted)]">
              Five specialists, five contracts, and the coordination on you.
            </p>
          </div>

          {/* Right — light, the new way */}
          <div
            className="rounded-3xl border-2 border-[var(--ember)]/45 bg-[var(--ember)]/[0.05] p-8 transition-all duration-500 ease-out sm:p-10"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(24px)",
              transitionDelay: inView ? "160ms" : "0ms",
            }}
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
              Installing The AI Dept.
            </p>
            <p
              style={{ fontSize: "clamp(44px, 6.5vw, 88px)", letterSpacing: "-0.03em", lineHeight: 1 }}
              className="mt-4 font-semibold text-[var(--ink)]"
              data-testid="price-right"
            >
              <span className="align-top text-lg font-medium text-[var(--muted)]">from </span>
              ${right.toLocaleString()}
              <span className="text-lg font-medium text-[var(--muted)]">/mo</span>
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-[var(--ink)]/80">
              One department. Every specialist, already coordinated and run for you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
